import React, { useState } from 'react';
import { Product, Category } from '../../types';
import { db } from '../../db';

interface SyncQueueItem {
  type: string;
  action: string;
  data: unknown;
  status: string;
  timestamp: number;
}

export function useProducts(
  addToSyncQueue: (item: SyncQueueItem) => Promise<unknown>,
  assertMutationAllowed: (fnName: string) => void,
  markQrProductDirty: (id: number) => void,
  markQrProductDeleted: (id: number) => void,
  markQrCategoryDirty: (id: string) => void,
  markQrCategoryDeleted: (id: string) => void
) {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const addProduct = async (item: Product) => {
    assertMutationAllowed('addProduct');
    const itemWithDocType = { ...item, docType: 'product' as const };
    const id = await db.products.add(itemWithDocType);
    const newItem = { ...itemWithDocType, id };
    await db.syncStack.add({ type: 'product', action: 'create', data: newItem, status: 'pending', timestamp: Date.now() });
    setProducts(prev => [newItem, ...prev]);
    markQrProductDirty(id as number);
  };

  const updateProduct = async (item: Product, skipSync = false) => {
    assertMutationAllowed('updateProduct');
    await db.products.put(item);
    if (!skipSync) {
      await addToSyncQueue({ type: 'product', action: 'update', data: item, status: 'pending', timestamp: Date.now() });
    }
    setProducts(prev => prev.map(i => i.id === item.id ? item : i));
    if (item.id) markQrProductDirty(item.id);
  };

  const deleteProduct = async (id: number) => {
    await db.products.delete(id);
    await db.syncStack.add({ type: 'product', action: 'delete', data: { id }, status: 'pending', timestamp: Date.now() });
    setProducts(prev => prev.filter(i => i.id !== id));
    markQrProductDirty(id);
    markQrProductDeleted(id);
  };

  const addCategory = async (category: Category) => {
    assertMutationAllowed('addCategory');
    await db.categories.add(category);
    await db.syncStack.add({ type: 'category', action: 'create', data: category, status: 'pending', timestamp: Date.now() });
    setCategories(prev => [...prev, category].sort((a, b) => a.sortOrder - b.sortOrder));
    markQrCategoryDirty(category.id);
  };

  const updateCategory = async (category: Category) => {
    assertMutationAllowed('updateCategory');
    await db.categories.put(category);
    await addToSyncQueue({ type: 'category', action: 'update', data: category, status: 'pending', timestamp: Date.now() });
    setCategories(prev => prev.map(c => c.id === category.id ? category : c).sort((a, b) => a.sortOrder - b.sortOrder));
    markQrCategoryDirty(category.id);
  };

  const deleteCategory = async (id: string) => {
    await db.categories.delete(id);
    await db.syncStack.add({ type: 'category', action: 'delete', data: { id }, status: 'pending', timestamp: Date.now() });
    setCategories(prev => prev.filter(c => c.id !== id));
    markQrCategoryDirty(id);
    markQrCategoryDeleted(id);
  };

  return {
    products,
    setProducts,
    categories,
    setCategories,
    addProduct,
    updateProduct,
    deleteProduct,
    addCategory,
    updateCategory,
    deleteCategory,
  };
}
